import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shell, PageHeader } from '../components/Shell.jsx';
import { Score } from '../components/Score.jsx';
import { Empty } from '../components/Empty.jsx';

const STATUS_LABELS = {
  discovered: { label: 'Discovered', cls: 'badge' },
  enriched:   { label: 'Enriched',   cls: 'badge' },
  scored:     { label: 'Scored',     cls: 'badge' },
  drafted:    { label: 'Drafted',    cls: 'badge badge-accent' },
  approved:   { label: 'Approved',   cls: 'badge badge-warning' },
  sent:       { label: 'Sent',       cls: 'badge badge-warning' },
  opened:     { label: 'Opened',     cls: 'badge badge-warning' },
  replied:    { label: 'Replied',    cls: 'badge badge-success' },
  booked:     { label: 'Booked',     cls: 'badge badge-success' },
  archived:   { label: 'Archived',   cls: 'badge' },
  skipped:    { label: 'Skipped',    cls: 'badge' },
  unsubscribed: { label: 'Unsub',    cls: 'badge badge-danger' },
};

export default function Prospects() {
  const [list, setList] = useState(null);

  useEffect(() => {
    fetch('/api/prospects?limit=200', { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(d => setList(d.prospects || []))
      .catch(() => setList([]));
  }, []);

  return (
    <Shell>
      <PageHeader
        eyebrow="The list"
        title="Prospects"
        sub="Every named human in your pipeline. Add manually or through discovery routines."
        right={<Link to="/prospects/new" className="btn">+ Add prospect</Link>}
      />

      {list === null ? (
        <div className="row muted"><span className="spinner" /> Loading prospects</div>
      ) : list.length === 0 ? (
        <Empty
          title="No prospects yet"
          body="Add the first person you want to reach. A journalist who writes about commercial roofing or building science is a great place to start."
          action={<Link to="/prospects/new" className="btn">+ Add your first prospect</Link>}
        />
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-soft)', borderBottom: '1px solid var(--border-soft)' }}>
                <Th>Name &amp; outlet</Th>
                <Th right>Fit</Th>
                <Th>Status</Th>
                <Th>Last contacted</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {list.map(p => (
                <tr key={p.id} style={{ borderBottom: '1px solid var(--border-soft)' }}>
                  <Td>
                    <div style={{ fontWeight: 500 }}>{p.name}</div>
                    <div className="muted" style={{ fontSize: 13 }}>
                      {[p.role, p.outlet].filter(Boolean).join(' · ') || <span className="faint">no outlet</span>}
                    </div>
                  </Td>
                  <Td right><Score value={p.fit_score} /></Td>
                  <Td>
                    {(() => {
                      const s = STATUS_LABELS[p.status] || { label: p.status, cls: 'badge' };
                      return <span className={s.cls}>{s.label}</span>;
                    })()}
                  </Td>
                  <Td>
                    <span className="muted" style={{ fontSize: 13 }}>{fmtDate(p.last_contacted_at) || 'never'}</span>
                  </Td>
                  <Td right>
                    <Link to={`/prospects/${p.id}`} className="btn btn-ghost btn-sm">View</Link>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Shell>
  );
}

function Th({ children, right }) {
  return (
    <th style={{
      textAlign: right ? 'right' : 'left',
      padding: '10px 16px',
      fontFamily: 'var(--font-mono)',
      fontSize: 11,
      fontWeight: 500,
      letterSpacing: '0.085em',
      textTransform: 'uppercase',
      color: 'var(--ink-muted)',
    }}>{children}</th>
  );
}

function Td({ children, right }) {
  return (
    <td style={{
      padding: '14px 16px',
      verticalAlign: 'middle',
      textAlign: right ? 'right' : 'left',
    }}>{children}</td>
  );
}

function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
